/* @jsx h */
import { defineComponent, ref, onMounted, h, computed } from "vue";
import { api } from "@app/data/api";
import { Loading } from "@app/vue/components/UIComponents";
import { ReportDetails } from "./ReportDetails";
import { BIReport } from "@lemmonhq/types";

type ReportEntry = BIReport & { disabled?: boolean; icon: string };

type ReportGroup = {
  id: string;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  reports: ReportEntry[];
};

const REPORT_ICONS: Record<string, string> = {
  revenue: "payments",
  category: "category",
  customers: "group",
  reservations: "calendar_month",
  reservation_summary: "event_available",
  reservation_details: "calendar_month",
  table_utilization: "table_restaurant",
};

const getReportIcon = (report: BIReport) => {
  const searchableName = `${report.id} ${report.name}`.toLowerCase();

  if (
    searchableName.includes("no-show") ||
    searchableName.includes("no_show")
  ) {
    return "person_off";
  }
  if (searchableName.includes("table")) return "table_restaurant";
  if (searchableName.includes("reservation")) return "calendar_month";

  return REPORT_ICONS[report.id] ?? "file_download";
};

const PLACEHOLDER_GROUPS: ReportGroup[] = [
  {
    id: "revenue",
    label: "Revenue & Sales",
    icon: "payments",
    color: "text-success",
    bgColor: "bg-success/10",
    reports: [
      {
        id: "daily_revenue",
        name: "Daily Revenue Summary",
        icon: "bar_chart",
        reportTypes: ["summary"],
        description: "Total revenue, taxes, discounts, and net sales per day.",
        disabled: true,
      },
      {
        id: "hourly_sales",
        name: "Hourly Sales Breakdown",
        icon: "schedule",
        reportTypes: ["detailed"],
        description: "Sales volume and revenue by hour of day.",
        disabled: true,
      },
      {
        id: "payment_methods",
        name: "Payment Methods",
        icon: "credit_card",
        reportTypes: ["summary", "detailed"],
        description: "Revenue split by cash, card, wallet, and other methods.",
        disabled: true,
      },
      {
        id: "discounts_voids",
        name: "Discounts & Voids",
        icon: "percent",
        reportTypes: ["detailed"],
        description: "All applied discounts, comps, and voided transactions.",
        disabled: true,
      },
    ],
  },
  {
    id: "orders",
    label: "Orders",
    icon: "receipt_long",
    color: "text-primary",
    bgColor: "bg-primary/10",
    reports: [
      {
        id: "order_summary",
        name: "Order Summary",
        icon: "receipt_long",
        reportTypes: ["summary", "detailed"],
        description: "Count and value of all orders by type and status.",
        disabled: true,
      },
      {
        id: "order_types",
        name: "Order Types",
        icon: "dinner_dining",
        reportTypes: ["summary"],
        description: "Dine-in, takeaway, delivery breakdown.",
        disabled: true,
      },
      {
        id: "cancellations",
        name: "Cancellations & Refunds",
        icon: "undo",
        reportTypes: ["detailed"],
        description: "Cancelled and refunded orders with reasons.",
        disabled: true,
      },
      {
        id: "avg_ticket",
        name: "Average Ticket Size",
        icon: "confirmation_number",
        reportTypes: ["summary"],
        description: "Average order value over a selected period.",
        disabled: true,
      },
    ],
  },
  {
    id: "menu",
    label: "Menu & Products",
    icon: "menu_book",
    color: "text-warning",
    bgColor: "bg-warning/10",
    reports: [
      {
        id: "top_products",
        name: "Top Selling Items",
        icon: "emoji_events",
        reportTypes: ["summary", "detailed"],
        description: "Best-performing menu items by quantity and revenue.",
        disabled: true,
      },
      {
        id: "category_sales",
        name: "Category Sales",
        icon: "category",
        reportTypes: ["summary"],
        description: "Sales broken down by menu category.",
        disabled: true,
      },
      {
        id: "modifier_usage",
        name: "Modifier Usage",
        icon: "tune",
        reportTypes: ["detailed"],
        description: "How often each modifier/add-on is selected.",
        disabled: true,
      },
    ],
  },
  {
    id: "customers",
    label: "Customers",
    icon: "group",
    color: "text-info",
    bgColor: "bg-info/10",
    reports: [
      {
        id: "new_vs_returning",
        name: "New vs Returning",
        icon: "person_add",
        reportTypes: ["summary"],
        description: "Breakdown of first-time versus repeat customer visits.",
        disabled: true,
      },
      {
        id: "customer_spend",
        name: "Customer Spend",
        icon: "account_balance_wallet",
        reportTypes: ["detailed"],
        description: "Per-customer lifetime spend and visit frequency.",
        disabled: true,
      },
      {
        id: "loyalty",
        name: "Loyalty Points",
        icon: "star",
        reportTypes: ["summary", "detailed"],
        description: "Points earned, redeemed, and outstanding balances.",
        disabled: true,
      },
    ],
  },
  {
    id: "reservations",
    label: "Reservations",
    icon: "event_seat",
    color: "text-secondary",
    bgColor: "bg-secondary/10",
    reports: [
      {
        id: "reservation_summary",
        name: "Reservation Summary",
        icon: "event_available",
        reportTypes: ["summary"],
        description: "Total bookings, covers, no-shows, and cancellations.",
        disabled: true,
      },
      {
        id: "table_utilization",
        name: "Table Utilization",
        icon: "table_restaurant",
        reportTypes: ["detailed"],
        description: "Turn times, occupancy rates, and peak hours per table.",
        disabled: true,
      },
    ],
  },
  {
    id: "staff",
    label: "Staff & Operations",
    icon: "badge",
    color: "text-accent",
    bgColor: "bg-accent/10",
    reports: [
      {
        id: "staff_sales",
        name: "Sales by Staff",
        icon: "leaderboard",
        reportTypes: ["summary", "detailed"],
        description: "Revenue and order count attributed to each staff member.",
        disabled: true,
      },
      {
        id: "shift_summary",
        name: "Shift Summary",
        icon: "work_history",
        reportTypes: ["summary"],
        description: "Sales totals, tips, and voids per shift.",
        disabled: true,
      },
      {
        id: "tips",
        name: "Tips Report",
        icon: "volunteer_activism",
        reportTypes: ["detailed"],
        description: "Tip amounts collected by staff and payment method.",
        disabled: true,
      },
    ],
  },
];

export const DataExportPage = defineComponent({
  name: "DataExportPage",
  setup() {
    const liveReports = ref<BIReport[]>([]);
    const selectedReport = ref<BIReport | null>(null);
    const isSheetOpen = ref(false);
    const isLoading = ref(true);
    const searchQuery = ref("");

    const handleReportSelect = (report: ReportEntry) => {
      if (report.disabled) return;
      selectedReport.value = report;
      isSheetOpen.value = true;
    };

    onMounted(async () => {
      try {
        liveReports.value = await api.getReports();
      } catch (error) {
        console.error("Failed to fetch reports:", error);
      } finally {
        isLoading.value = false;
      }
    });

    const groups = computed<ReportGroup[]>(() => {
      const query = searchQuery.value.trim().toLowerCase();

      const liveGroup: ReportGroup | null =
        liveReports.value.length > 0
          ? {
              id: "live",
              label: "Available Now",
              icon: "download_for_offline",
              color: "text-success",
              bgColor: "bg-success/10",
              reports: liveReports.value.map((r) => ({
                ...r,
                icon: getReportIcon(r),
                disabled: false,
              })),
            }
          : null;

      const all: ReportGroup[] = [
        ...(liveGroup ? [liveGroup] : []),
        ...PLACEHOLDER_GROUPS,
      ];

      if (!query) return all;

      return all
        .map((g) => ({
          ...g,
          reports: g.reports.filter(
            (r) =>
              r.name.toLowerCase().includes(query) ||
              (r.description ?? "").toLowerCase().includes(query),
          ),
        }))
        .filter((g) => g.reports.length > 0);
    });

    return () => (
      <div class="h-full min-h-0 w-full overflow-y-auto">
        <div class="sticky top-0 border-b border-neutral/15 glossy-bg backdrop-blur-sm">
          <div class="mx-auto flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div class="flex items-center gap-3">
              <div class="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <span class="material-symbols-outlined text-3xl">
                  file_export
                </span>
              </div>
              <h1 class="text-2xl text-accent">Export reports</h1>
            </div>

            <div class="relative w-full sm:w-72">
              <span class="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-base-content/40">
                search
              </span>
              <input
                type="search"
                value={searchQuery.value}
                onInput={(e) => {
                  searchQuery.value = (e.target as HTMLInputElement).value;
                }}
                placeholder="Search reports…"
                class="input input-bordered w-full rounded-lg border-neutral/20 bg-base-100 pl-10 pr-10 text-sm focus:border-primary focus:outline-none"
              />
              {searchQuery.value.trim().length > 0 && (
                <button
                  aria-label="Clear search"
                  class="btn btn-circle btn-ghost btn-xs absolute right-3 top-1/2 -translate-y-1/2 text-base-content/50"
                  onClick={() => {
                    searchQuery.value = "";
                  }}
                >
                  <span class="material-symbols-outlined text-base">close</span>
                </button>
              )}
            </div>
          </div>
        </div>

        <div class="mx-auto px-4 py-6 sm:px-6">
          {isLoading.value ? (
            <div class="flex justify-center items-center min-h-[300px]">
              <Loading />
            </div>
          ) : groups.value.length === 0 ? (
            <div class="flex min-h-[240px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-neutral/20 text-base-content/40">
              <span class="material-symbols-outlined text-3xl">search_off</span>
              <p class="text-sm text-base-content/60">
                No reports match "{searchQuery.value.trim()}"
              </p>
            </div>
          ) : (
            <div class="space-y-8">
              {groups.value.map((group) => (
                <section key={group.id}>
                  <div class="mb-3 flex items-center gap-2">
                    <span
                      class={`material-symbols-outlined text-lg ${group.color}`}
                    >
                      {group.icon}
                    </span>
                    <h2 class="text-sm font-semibold text-base-content">
                      {group.label}
                    </h2>
                  </div>

                  <div class="divide-y divide-base-content/10 overflow-hidden rounded-lg border border-neutral/20 bg-base-100">
                    {group.reports.map((report) => (
                      <button
                        key={report.id}
                        onClick={() => handleReportSelect(report)}
                        disabled={report.disabled}
                        class={[
                          "group flex w-full items-center gap-4 p-4 text-left transition-colors sm:px-5",
                          report.disabled
                            ? "cursor-not-allowed opacity-45"
                            : "hover:bg-base-200/60",
                        ].join(" ")}
                      >
                        <div
                          class={`flex size-10 shrink-0 items-center justify-center rounded-lg ${group.bgColor}`}
                        >
                          <span
                            class={`material-symbols-outlined text-lg ${group.color}`}
                          >
                            {report.icon}
                          </span>
                        </div>

                        <div class="min-w-0 flex-1">
                          <p class="text-sm font-medium text-base-content">
                            {report.name}
                          </p>
                          {report.description && (
                            <p class="mt-1 text-xs leading-relaxed text-base-content/50">
                              {report.description}
                            </p>
                          )}
                        </div>
                        <span class="material-symbols-outlined ml-auto shrink-0 text-xl text-base-content/35 transition-transform group-hover:translate-x-0.5 group-hover:text-base-content/60">
                          chevron_right
                        </span>
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>

        {selectedReport.value && (
          <ReportDetails
            report={selectedReport.value}
            isOpen={isSheetOpen.value}
            onClose={() => {
              isSheetOpen.value = false;
              selectedReport.value = null;
            }}
          />
        )}
      </div>
    );
  },
});
